/**
 * @file InventoryControlPanelPage.jsx
 * @description INVENTORY — Control Panel. The spare parts the upcoming
 *              services and maintenance will use, read from each machine's own
 *              Dingli parts manual and checked against the stock we hold, most
 *              important first.
 *
 *              Row 1  KPI tiles — only the ones that call for action carry
 *                     colour, and only when they are above zero.
 *              Row 2  Parts to have ready, ranked by importance.
 *              Row 3  Upcoming services, machine by machine, with whether
 *                     their parts are on the shelf.
 *              Row 4  Low stock — at or under the reorder level.
 *              Footer How the parts are picked (the server's own rule table).
 *
 *              Data: GET /api/inventory/control-panel (one call, 60s server
 *              cache; the rules live in server/utils/serviceParts.js). Nothing
 *              here is filled in: a part no manual numbers, a machine with no
 *              manual, or a part missing from the stock list says exactly that.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Typography, Card, Stack, Chip, IconButton, Tooltip, Alert, Button, Link,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Skeleton,
    CircularProgress,
} from '@mui/material';
import {
    Refresh as RefreshIcon, Inventory2Outlined as PartsIcon,
    EventAvailableOutlined as ServiceIcon, CheckCircleOutline as StockOkIcon,
} from '@mui/icons-material';
import inventoryService from '../../../services/inventoryService';
import { formatToIST } from '../../../utils/dataUtils';

// Colour never travels alone — every state carries its word.
const IMPORTANCE = {
    CRITICAL: { color: 'error', label: 'Critical' },
    HIGH: { color: 'warning', label: 'High' },
    MEDIUM: { color: 'info', label: 'Medium' },
    LOW: { color: 'success', label: 'Low' },
};
const SERVICE_STATUS = {
    OVERDUE: { color: 'error', label: 'Overdue' },
    DUE: { color: 'warning', label: 'Due' },
    SCHEDULED: { color: 'info', label: 'Scheduled' },
};
const READINESS = {
    READY: { color: 'success', label: () => 'Parts ready' },
    SHORT: { color: 'error', label: (s) => `${s.parts_short} of ${s.parts_needed} short` },
    PARTIAL: { color: 'warning', label: () => 'In stock · some unknown' },
    NOT_DETERMINABLE: { color: 'default', label: () => 'Not determinable' },
    NO_PARTS: { color: 'default', label: () => 'No parts used' },
};

// Tile emphasis: 'alert' and 'warn' only when the number calls for action.
const TONES = {
    plain: { box: { bgcolor: 'grey.100' }, value: 'text.primary', label: 'text.secondary' },
    alert: { box: { bgcolor: 'error.light', borderBottom: 4, borderColor: 'error.main' }, value: 'error.dark', label: 'error.dark' },
    warn: { box: { bgcolor: 'warning.light' }, value: 'warning.dark', label: 'warning.dark' },
};

const num = (n, digits = 1) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: digits });

const fmtIST = (ts, pattern) => {
    if (!ts) return '—';
    const out = formatToIST(ts, pattern);
    return out === 'N/A' || out === 'Invalid Date' ? '—' : out;
};

/** "30 h over · 12 d over" / "in 40 h · in 9 d" — the PM schedule's two clocks. */
const dueText = (hmr, days) => {
    const bits = [];
    if (hmr != null) bits.push(hmr < 0 ? `${num(-hmr)} h over` : `in ${num(hmr)} h`);
    if (days != null) bits.push(days < 0 ? `${num(-days, 0)} d over` : days === 0 ? 'today' : `in ${num(days, 0)} d`);
    return bits.length ? bits.join(' · ') : 'No meter reading or date on record';
};

/** Which engine a part is for, when the manual prints it for only one. */
const variantNote = (p) => {
    const tagged = p.machines.filter((m) => m.variant);
    if (!tagged.length) return null;
    const v = [...new Set(tagged.map((m) => m.variant))].join(' / ');
    return tagged.length === p.machines.length
        ? `${v} only — needed if that engine is fitted`
        : `${v} only on ${tagged.length} of ${p.machines.length} machines`;
};

const Tile = ({ value, label, tone, sub, hint }) => {
    const t = TONES[tone] || TONES.plain;
    return (
        <Tooltip describeChild title={hint || ''} disableHoverListener={!hint}>
            <Box sx={{ ...t.box, borderRadius: 2, p: 2, textAlign: 'center' }}>
                <Typography className="tnum" sx={{ fontSize: 'clamp(1.6rem, 2.2vw, 2.5rem)', fontWeight: 800, lineHeight: 1.15, color: t.value }}>
                    {value == null ? '—' : num(value, 0)}
                </Typography>
                <Typography variant="overline" sx={{ display: 'block', lineHeight: 1.4, mt: 0.5, color: t.label }}>
                    {label}
                </Typography>
                {sub && <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: t.label }}>{sub}</Typography>}
            </Box>
        </Tooltip>
    );
};

const Section = ({ title, count, caption, children }) => (
    <Card sx={{ p: { xs: 2, md: 2.5 } }}>
        <Box sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h6">{title}</Typography>
                {count != null && <Chip size="small" label={count} />}
            </Stack>
            {caption && <Typography variant="body2" color="text.secondary">{caption}</Typography>}
        </Box>
        {children}
    </Card>
);

const Empty = ({ icon: Icon, title, text }) => (
    <Box sx={{ py: 5, textAlign: 'center' }}>
        <Icon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={700} color="text.secondary">{title}</Typography>
        {text && <Typography variant="body2" color="text.secondary">{text}</Typography>}
    </Box>
);

const StatusChip = ({ status }) => {
    const s = SERVICE_STATUS[status] || { color: 'default', label: status || '—' };
    return <Chip size="small" color={s.color} label={s.label} />;
};

const MachineChips = ({ machines }) => {
    const shown = machines.slice(0, 4);
    const rest = machines.slice(4);
    const notCovered = machines.filter((m) => !m.covered).length;
    return (
        <Box>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
                {shown.map((m) => (
                    <Tooltip key={m.serial_no} describeChild
                        title={`${(SERVICE_STATUS[m.status] || {}).label || m.status} · needs ${m.qty}${m.variant ? ` if ${m.variant}` : ''} · ${m.covered ? 'covered by stock' : 'not covered'}`}>
                        <Chip size="small" variant="outlined" color={m.covered ? 'default' : 'error'} label={m.serial_no} />
                    </Tooltip>
                ))}
                {rest.length > 0 && (
                    <Tooltip describeChild title={rest.map((m) => m.serial_no).join(', ')}>
                        <Chip size="small" label={`+${rest.length} more`} />
                    </Tooltip>
                )}
            </Stack>
            <Typography variant="caption" color={notCovered ? 'error.dark' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
                {notCovered ? `${notCovered} of ${machines.length} not covered` : `All ${machines.length} covered`}
            </Typography>
        </Box>
    );
};

const PartsTable = ({ parts }) => (
    <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: 190 }}>Importance</TableCell>
                    <TableCell>Part no.</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>For machines</TableCell>
                    <TableCell align="right">Needed</TableCell>
                    <TableCell align="right">On hand</TableCell>
                    <TableCell align="right">Short</TableCell>
                    <TableCell>Earliest due</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {parts.map((p) => {
                    const imp = IMPORTANCE[p.importance] || { color: 'default', label: p.importance };
                    const vNote = variantNote(p);
                    return (
                        <TableRow key={p.part_number} hover>
                            <TableCell>
                                <Chip size="small" color={imp.color} label={imp.label} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {p.reason}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Tooltip describeChild title="Open in the parts catalogue">
                                    <Link component={RouterLink} to={`/parts-catalog?q=${encodeURIComponent(p.part_number)}`}
                                        underline="hover" fontWeight={700} className="tnum">
                                        {p.part_number}
                                    </Link>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" fontWeight={600}>{p.description}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {p.kind_label} · job {p.checklist}
                                </Typography>
                                {vNote && <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>{vNote}</Typography>}
                                {p.note && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{p.note}</Typography>}
                            </TableCell>
                            <TableCell sx={{ minWidth: 220 }}><MachineChips machines={p.machines} /></TableCell>
                            <TableCell align="right" className="tnum">{num(p.needed_qty, 0)}</TableCell>
                            <TableCell align="right" className="tnum">
                                {num(p.on_hand, 0)}{p.unit ? ` ${p.unit}` : ''}
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'nowrap' }}>
                                    {p.stocked ? p.item_code : 'Not in stock list'}
                                </Typography>
                            </TableCell>
                            <TableCell align="right" className="tnum">
                                {p.short_by > 0
                                    ? <Typography variant="body2" fontWeight={700} color="error.dark">{num(p.short_by, 0)}</Typography>
                                    : <Typography variant="body2" color="text.secondary">—</Typography>}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <StatusChip status={p.earliest_due.status} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {p.earliest_due.serial_no} · {dueText(p.earliest_due.remaining_hmr, p.earliest_due.remaining_days)}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </TableContainer>
);

const ServicesTable = ({ services }) => (
    <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Machine</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>Service</TableCell>
                    <TableCell>Due</TableCell>
                    <TableCell>Due date</TableCell>
                    <TableCell>Parts</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {services.map((s) => {
                    const r = READINESS[s.readiness] || { color: 'default', label: () => s.readiness };
                    return (
                        <TableRow key={s.serial_no} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <Typography variant="body2" fontWeight={700}>{s.serial_no}</Typography>
                                <Typography variant="caption" color="text.secondary">{s.model || 'Model not recorded'}</Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 260 }}>
                                <Typography variant="body2" noWrap title={s.site || ''}>{s.site || '—'}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" fontWeight={600}>
                                    {s.next_service_plan || 'No plan'}
                                    {s.service_hours ? ` · ${num(s.service_hours, 0)} h service` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {s.jobs.length ? s.jobs.map((j) => j.code).join(' · ')
                                        : s.service_hours ? 'No part-replacing jobs' : 'Hour mark not on record'}
                                    {s.hours_basis === 'interval' ? ' (from the service interval)' : ''}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                <StatusChip status={s.status} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                    {dueText(s.remaining_hmr, s.remaining_days)}
                                </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtIST(s.next_due_date, 'dd MMM yyyy')}</TableCell>
                            <TableCell sx={{ minWidth: 220 }}>
                                <Chip size="small" color={r.color} variant={r.color === 'default' ? 'outlined' : 'filled'} label={r.label(s)} />
                                {s.unresolved.map((u, i) => (
                                    <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        {u.kind ? `${u.label}: ` : ''}{u.reason}
                                    </Typography>
                                ))}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </TableContainer>
);

const LowStockTable = ({ items }) => (
    <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Item code</TableCell>
                    <TableCell>Part no.</TableCell>
                    <TableCell>Item</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">On hand</TableCell>
                    <TableCell align="right">Reorder at</TableCell>
                    <TableCell />
                </TableRow>
            </TableHead>
            <TableBody>
                {items.map((it) => (
                    <TableRow key={it.item_code} hover>
                        <TableCell className="tnum" sx={{ whiteSpace: 'nowrap' }}>{it.item_code}</TableCell>
                        <TableCell className="tnum" sx={{ whiteSpace: 'nowrap' }}>{it.part_number || '—'}</TableCell>
                        <TableCell>{it.item_name || '—'}</TableCell>
                        <TableCell>{it.category || '—'}</TableCell>
                        <TableCell align="right" className="tnum">{num(it.current_stock, 0)}{it.unit ? ` ${it.unit}` : ''}</TableCell>
                        <TableCell align="right" className="tnum">{it.reorder_level == null ? '—' : num(it.reorder_level, 0)}</TableCell>
                        <TableCell>
                            <Stack direction="row" gap={0.5} flexWrap="wrap">
                                {it.needed_for_service && <Chip size="small" color="warning" label="Needed for upcoming service" />}
                                {it.is_critical && <Chip size="small" color="error" variant="outlined" label="Critical part" />}
                            </Stack>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

const LoadingRows = () => (
    <Box>{[...Array(5)].map((_, i) => <Skeleton key={i} height={44} />)}</Box>
);

const InventoryControlPanelPage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const alive = useRef(true);

    const load = useCallback(async (force = false) => {
        setRefreshing(true);
        try {
            const r = await inventoryService.getControlPanel(force);
            if (!alive.current) return;
            setData(r);
            setError('');
        } catch (e) {
            if (alive.current) setError(e.message || 'Could not load the inventory control panel.');
        } finally {
            if (alive.current) { setLoading(false); setRefreshing(false); }
        }
    }, []);

    useEffect(() => {
        alive.current = true;
        load(false);
        const id = setInterval(() => load(false), 60_000);
        return () => { alive.current = false; clearInterval(id); };
    }, [load]);

    const s = data?.summary || {};
    const horizon = data?.horizon || {};
    const parts = data?.parts || [];
    const services = data?.services || [];
    const lowStock = data?.low_stock || [];
    const notes = data?.notes || [];
    const sources = data?.sources || {};

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }} className="animate-fade-in">
            <Stack spacing={{ xs: 2, md: 3 }} sx={{ maxWidth: 1920, mx: 'auto' }}>
                {/* Refresh — no page title, the top bar already names the page */}
                <Stack direction="row" justifyContent="flex-end">
                    <Stack direction="row" alignItems="center" spacing={1}>
                        {data && (
                            <Typography variant="caption" color="text.secondary">
                                Updated {fmtIST(data.generated_at, 'dd MMM · HH:mm')} IST
                            </Typography>
                        )}
                        <Tooltip title="Refresh now">
                            <span>
                                <IconButton aria-label="Refresh" size="small" onClick={() => load(true)} disabled={refreshing}>
                                    {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* First load failed: nothing to show but the error. */}
                {error && !data && !loading && (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => load(true)}>Retry</Button>}>
                        {error}
                    </Alert>
                )}
                {error && data && (
                    <Alert severity="warning">
                        Could not refresh ({error}) — showing the figures from {fmtIST(data.generated_at, 'HH:mm')} IST.
                    </Alert>
                )}
                {notes.length > 0 && (
                    <Alert severity="warning">
                        {notes.map((n, i) => <Box key={i}>{n}</Box>)}
                    </Alert>
                )}

                {/* Row 1 — KPI tiles */}
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' } }}>
                    {loading ? [...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={104} />) : (
                        <>
                            <Tile label="Services overdue" value={s.services_overdue} tone={s.services_overdue > 0 ? 'alert' : 'plain'}
                                hint="Past their service hours or date on the PM schedule" />
                            <Tile label="Services due soon" value={s.services_due} tone={s.services_due > 0 ? 'warn' : 'plain'}
                                hint="Within 50 engine hours or 14 days" />
                            <Tile label={`Scheduled · ${horizon.days || 60} days`} value={s.services_scheduled} tone="plain"
                                hint={horizon.description} />
                            <Tile label="Parts to order" value={s.parts_short} tone={s.parts_short > 0 ? 'alert' : 'plain'}
                                sub={s.units_short > 0 ? `${num(s.units_short, 0)} units short` : null}
                                hint="Part numbers where stock does not cover every upcoming service" />
                            <Tile label="Critical parts" value={s.critical_parts} tone={s.critical_parts > 0 ? 'alert' : 'plain'}
                                hint="An overdue service is left without the part" />
                            <Tile label="Low-stock items" value={s.low_stock_items} tone={s.low_stock_items > 0 ? 'warn' : 'plain'}
                                sub={s.low_stock_critical > 0 ? `${num(s.low_stock_critical, 0)} critical` : null}
                                hint="At or under the reorder level, across the whole stock list" />
                        </>
                    )}
                </Box>

                {/* Row 2 — parts to have ready */}
                <Section
                    title="Parts to have ready"
                    count={loading ? null : parts.length}
                    caption={`Every filter the next services replace, ranked by importance: Critical — an overdue service is left without it; High — a due service is, or an overdue one needs it now; Medium — only scheduled services are; Low — stock covers them all.${horizon.description ? ` Window: ${horizon.description.toLowerCase()}.` : ''}`}
                >
                    {loading ? <LoadingRows /> : parts.length ? <PartsTable parts={parts} /> : (
                        <Empty icon={PartsIcon}
                            title={services.length ? 'No part numbers could be read for these services' : 'No services coming up'}
                            text={services.length
                                ? 'See "Upcoming services" below for why each machine’s parts are not determinable.'
                                : sources.pm_schedule === 'ok' ? 'Nothing on the PM schedule falls inside the horizon.' : 'The PM schedule could not be read.'} />
                    )}
                </Section>

                {/* Row 3 — the services themselves */}
                <Section
                    title="Upcoming services"
                    count={loading ? null : services.length}
                    caption={loading ? null : `From the PM schedule, most urgent first${s.services_beyond_horizon ? ` · ${num(s.services_beyond_horizon, 0)} more fall due later` : ''}. "In stock · some unknown" means every part we could identify is covered, but the manual names no part for something the service does.`}
                >
                    {loading ? <LoadingRows /> : services.length ? <ServicesTable services={services} /> : (
                        <Empty icon={ServiceIcon} title="No services coming up"
                            text={sources.pm_schedule === 'ok' ? 'Nothing on the PM schedule falls inside the horizon.' : 'The PM schedule could not be read.'} />
                    )}
                </Section>

                {/* Row 4 — low stock */}
                <Section
                    title="Low stock"
                    count={loading || s.low_stock_items == null ? null : num(s.low_stock_items, 0)}
                    caption={loading ? null : s.low_stock_items > lowStock.length
                        ? `At or under the reorder level. Showing ${num(lowStock.length, 0)} of ${num(s.low_stock_items, 0)} — parts the upcoming services need first, then critical parts, then the largest shortfall.`
                        : 'At or under the reorder level — parts the upcoming services need first.'}
                >
                    {loading ? <LoadingRows /> : lowStock.length ? <LowStockTable items={lowStock} /> : (
                        <Empty icon={StockOkIcon}
                            title={sources.inventory === 'ok' ? 'Nothing is at or under its reorder level' : 'Stock list unavailable'}
                            text={sources.inventory === 'ok' ? null : 'The stock table could not be read.'} />
                    )}
                </Section>

                {/* Footer — the rule table, straight from the server */}
                {!loading && data && (
                    <Card sx={{ p: { xs: 2, md: 2.5 } }}>
                        <Typography variant="subtitle1" fontWeight={700}>How the parts are picked</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            A service at N hours does every checklist job whose interval divides N (the 500 h service also does the 250 h jobs).
                            The part numbers come from the machine&apos;s own Dingli parts manual; stock is matched on the manufacturer part number.
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {(data.rules || []).map((r) => (
                                <Typography component="li" variant="body2" key={r.kind}>
                                    <b>{r.label}</b> — job {r.checklist} &ldquo;{r.title}&rdquo;, every {num(r.every_hours, 0)} h
                                </Typography>
                            ))}
                        </Box>
                        {(data.not_counted || []).length > 0 && (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Not counted:</Typography>
                                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                    {data.not_counted.map((n, i) => (
                                        <Typography component="li" variant="body2" color="text.secondary" key={i}>{n}</Typography>
                                    ))}
                                </Box>
                            </>
                        )}
                    </Card>
                )}
            </Stack>
        </Box>
    );
};

export default InventoryControlPanelPage;
